import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

export type VerbatimSentiment = 'positive' | 'negative' | 'neutral';

export interface VerbatimItem {
  text: string;
  sentiment: VerbatimSentiment;
  rowChild: string;
}

@Component({
  selector: 'app-verbatim-chart',
  templateUrl: './verbatim.component.html',
  styleUrls: ['./verbatim.component.css'],
})
export class VerbatimComponent implements OnChanges, OnDestroy {
  /** Hard cap on rendered statements; anything beyond this is discarded. */
  static readonly MAX_ITEMS = 30;

  @Input() customData: any;
  @Input() isOutput: any = false;
  @Input() popUp: any = false;
  @Input() id: any = 0;
  @Input() sort: any;
  @Input() height: any = '24em';
  @Input() WidgetDetails: any;

  items: VerbatimItem[] = [];
  title: string = '';

  private tooltipEl: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['customData']) {
      this.title = this.customData?.widget_name ?? '';
      this.items = this.buildItems(this.customData);
      this.removeTooltip(true);
    }
  }

  ngOnDestroy() {
    this.removeTooltip(true);
  }

  trackByIndex(index: number): number {
    return index;
  }

  showHover(event: MouseEvent, item: VerbatimItem) {
    const card = event.currentTarget as HTMLElement;
    const textEl = card.querySelector('.verbatim-item-text') as HTMLElement | null;
    if (!textEl || !this.isTextTruncated(textEl)) {
      this.hideHover();
      return;
    }

    this.removeTooltip(true);
    const rect = card.getBoundingClientRect();
    const tooltip = this.renderer.createElement('div');
    const maxWidth = Math.min(450, window.innerWidth - 32);
    const gap = 6;
    let left = rect.left;
    if (left + maxWidth > window.innerWidth - 16) {
      left = Math.max(16, window.innerWidth - maxWidth - 16);
    }

    this.renderer.setProperty(tooltip, 'textContent', item.text);
  this.applyTooltipStyles(tooltip, maxWidth);

    if (rect.bottom + gap > window.innerHeight - 80) {
      this.renderer.setStyle(tooltip, 'top', 'auto');
      this.renderer.setStyle(tooltip, 'bottom', `${window.innerHeight - rect.top + gap}px`);
    } else {
      this.renderer.setStyle(tooltip, 'top', `${rect.bottom + gap}px`);
      this.renderer.setStyle(tooltip, 'bottom', 'auto');
    }
    this.renderer.setStyle(tooltip, 'left', `${left}px`);
    this.renderer.appendChild(document.body, tooltip);
    this.tooltipEl = tooltip;
    requestAnimationFrame(() => {
      if (this.tooltipEl !== tooltip) {
        return;
      }
      this.renderer.setStyle(tooltip, 'opacity', '1');
      this.renderer.setStyle(tooltip, 'transform', 'translateY(0)');
    });
  }

  hideHover() {
    if (!this.tooltipEl) {
      return;
    }
    const tooltip = this.tooltipEl;
    this.renderer.setStyle(tooltip, 'opacity', '0');
    this.renderer.setStyle(tooltip, 'transform', 'translateY(4px)');
    this.hideTimer = setTimeout(() => {
      this.removeTooltip(false);
    }, 160);
  }

  onListScroll() {
    this.removeTooltip(true);
  }

  private applyTooltipStyles(tooltip: HTMLElement, maxWidth: number) {
    const styles: { [key: string]: string } = {
      position: 'fixed',
      width: 'max-content',
      maxWidth: `${maxWidth}px`,
      boxSizing: 'border-box',
      padding: '0.5rem 0.7rem',
      borderRadius: '0.25rem',
      borderTop: '2px solid var(--font-color1, #8600CD)',
     background: '#262626',
      color: 'var(--PrimaryColors-White, #FFF)',
      fontFamily: 'KantarBrown-Regular',
      fontSize: '0.6875rem',
      fontWeight: '400',
      lineHeight: '1rem',
      left: '2rem',
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      boxShadow: '0px 8px 8px 0px rgba(0, 0, 0, 0.16)',
      zIndex: '10001',
      pointerEvents: 'none',
      opacity: '0',
      transform: 'translateY(4px)',
      transition: 'opacity 160ms ease, transform 160ms ease',
    };
    Object.keys(styles).forEach((key) => this.renderer.setStyle(tooltip, key, styles[key]));
  }

  private isTextTruncated(textEl: HTMLElement): boolean {
    const probe = textEl.cloneNode(true) as HTMLElement;
    probe.style.webkitLineClamp = 'unset';
    probe.style.maxHeight = 'none';
    probe.style.height = 'auto';
    probe.style.overflow = 'visible';
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.display = 'block';
    probe.style.width = `${textEl.clientWidth}px`;
    textEl.parentElement?.appendChild(probe);
    const truncated = probe.scrollHeight > textEl.clientHeight + 1;
    probe.remove();
    return truncated;
  }

  private removeTooltip(immediate: boolean) {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (!this.tooltipEl) {
      return;
    }
    if (this.tooltipEl.parentNode) {
      this.renderer.removeChild(document.body, this.tooltipEl);
    }
    this.tooltipEl = null;
  }

  private buildItems(widget: any): VerbatimItem[] {
    const metricValues = widget?.metric?.[0]?.metric_value;
    if (!Array.isArray(metricValues)) {
      return [];
    }

    const items: VerbatimItem[] = [];
    for (const metricValue of metricValues) {
      if (items.length >= VerbatimComponent.MAX_ITEMS) {
        break;
      }
      const dataPoints = Array.isArray(metricValue?.data) ? metricValue.data : [];
      const point = dataPoints[0];
      const text = (metricValue?.option ?? '').toString().trim();
      if (!text || !point) {
        continue;
      }
      items.push({
        text,
        rowChild: point?.rowChild ?? '',
        sentiment: this.resolveSentiment(point?.colChild),
      });
    }
    return items;
  }

  private resolveSentiment(colChild: string): VerbatimSentiment {
    const normalized = (colChild ?? '').toLowerCase();
    if (normalized.includes('positive')) {
      return 'positive';
    }
    if (normalized.includes('negative')) {
      return 'negative';
    }
    return 'neutral';
  }
}
